import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Divider,
  useMediaQuery,
  useTheme,
  Fab
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import { useGuide } from '../../context/GuideContext';

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { startGuide } = useGuide();

  // /user-chat 라우트는 팝업 모드 — 네비 버튼 숨기고 X 버튼만 표시
  const isPopupMode = location.pathname === '/user-chat';

  const menuItems = [
    { text: '홈', icon: <HomeIcon />, path: '/' },
    { text: 'AI 질의응답', icon: <ChatIcon />, path: '/user-chat' },  // 사용자용 채팅 (/user-chat)
    // { text: 'AI 질의응답 (관리자용)', icon: <ChatIcon />, path: '/chat' },  // 기존 /chat 는 관리자 참고용
  ];

  // 스크롤 이벤트 처리
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleStartGuide = () => {
    // 현재 경로에 따라 다른 가이드 시작
    if (location.pathname === '/') {
      startGuide('home');
    } else if (location.pathname === '/user-chat' || location.pathname === '/chat') {
      startGuide('chat');
    } else if (location.pathname === '/cases') {
      startGuide('cases');
    }
  };

  const drawer = (
    <Box
      sx={{ 
        width: 280,
        background: 'linear-gradient(135deg, #263238 0%, #37474F 100%)',
        color: 'white',
        height: '100%'
      }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mb: 2
          }}
        >
          <img 
            src="https://www.koreamedis.com/images/logo.png" 
            alt="KOREA MEDIS 로고" 
            style={{ 
              height: '30px',
              display: 'block'
            }}
          />
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
      <List sx={{ px: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={isActive(item.path)}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: '8px',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    fontWeight: isActive(item.path) ? 700 : 500,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    color: 'white'
                  } 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', mx: 2 }} />
      <List sx={{ px: 2 }}>
        <ListItem disablePadding sx={{ mt: 1 }}>
          <ListItemButton 
            onClick={() => navigate('/admin')} 
            data-guide="admin-button"
            sx={{
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText 
              primary="관리자 메뉴" 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  fontWeight: 500,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  color: 'white'
                } 
              }} 
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" elevation={0} sx={{ 
        backgroundColor: 'white', 
        backgroundImage: 'none',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <Toolbar sx={{ py: 1, position: 'relative' }}>
          {isMobile && !isPopupMode && (
            <IconButton
              edge="start"
              aria-label="menu"
              sx={{ 
                mr: 2,
                color: '#2D3748'
              }}
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: isPopupMode ? 0 : 1, flexShrink: 0 }}>
            <img 
              src="https://www.koreamedis.com/images/logo.png" 
              alt="KOREA MEDIS 로고" 
              style={{ 
                height: '20px',
                display: 'block',
                cursor: isPopupMode ? 'default' : 'pointer'
              }}
              onClick={isPopupMode ? undefined : () => navigate('/')}
            />
          </Box>
          
          {!isMobile && !isPopupMode && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  onClick={() => navigate(item.path)}
                  startIcon={item.icon}
                  sx={{
                    fontWeight: isActive(item.path) ? 'bold' : 'normal',
                    borderRadius: '6px',
                    px: 2.5,
                    py: 0.8,
                    backgroundColor: isActive(item.path) ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    color: isActive(item.path) ? '#7c3aed' : '#2D3748',
                    border: `1px solid ${isActive(item.path) ? 'rgba(139, 92, 246, 0.3)' : 'rgba(45, 55, 72, 0.2)'}`,
                    '&:hover': {
                      backgroundColor: 'rgba(139, 92, 246, 0.08)',
                      transform: 'translateY(-1px)',
                      borderColor: 'rgba(139, 92, 246, 0.3)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                onClick={() => navigate('/admin')}
                data-guide="admin-button"
                startIcon={<AdminPanelSettingsIcon />}
                sx={{
                  borderRadius: '6px',
                  px: 2.5,
                  py: 0.8,
                  backgroundColor: 'transparent',
                  color: '#2D3748',
                  border: '1px solid rgba(45, 55, 72, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 92, 246, 0.08)',
                    transform: 'translateY(-1px)',
                    borderColor: 'rgba(139, 92, 246, 0.3)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                관리자
              </Button>
              <IconButton
                onClick={handleStartGuide}
                sx={{ 
                  ml: 1,
                  backgroundColor: 'transparent',
                  color: '#2D3748',
                  border: '1px solid rgba(45, 55, 72, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 92, 246, 0.08)',
                    transform: 'scale(1.05)',
                    borderColor: 'rgba(139, 92, 246, 0.3)',
                  },
                  transition: 'all 0.2s ease'
                }}
                aria-label="가이드 보기"
                style={{
                  display: 'none'
                }}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Box>
          )}

          {/* 팝업 모드: 헤더 가운데 이메일 표시 */}
          {isPopupMode && (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                px: 1,
              }}
            >
              <Typography
                component="a"
                href="mailto:info@koreamedis.com"
                sx={{
                  fontSize: { xs: '12px', sm: '15px' },
                  color: '#4A5568',
                  fontWeight: 500,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&:hover': { color: '#2D3748', textDecoration: 'underline' },
                }}
              >
                info@koreamedis.com
              </Typography>
            </Box>
          )}

          {/* 팝업 모드: X 버튼만 표시 */}
          {isPopupMode && (
            <IconButton
              onClick={() => {
                try {
                  // 1. iframe 위젯 안에 임베드되어 있을 때: 부모 창으로 닫기 신호 전송 후 즉시 종료
                  if (window.parent && window.parent !== window) {
                    window.parent.postMessage({ type: 'KOREAMEDIS_CHAT_CLOSE' }, '*');
                    window.parent.postMessage('KOREAMEDIS_CHAT_CLOSE', '*');
                    return; // 뒤로가기 절대 실행 안 함!
                  }
                } catch (e) {
                  console.error('postMessage error:', e);
                }

                // 2. 팝업 창으로 열렸을 때 (window.open)
                if (window.opener) {
                  window.close();
                  return;
                }

                // 3. 브라우저 단독 탭으로 열렸을 때만 뒤로가기
                window.history.back();
              }}
              aria-label="팝업 닫기"
              sx={{
                color: '#2D3748',
                border: '1px solid rgba(45, 55, 72, 0.2)',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  borderColor: 'rgba(220, 38, 38, 0.4)',
                  color: '#dc2626',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            background: 'linear-gradient(135deg, #263238 0%, #37474F 100%)',
          }
        }}
      >
        {drawer}
      </Drawer>
      
      <Box sx={{ 
        minHeight: isPopupMode ? 'calc(100vh - 60px)' : 'calc(100vh - 140px)',
        background: 'transparent',
        overflowX: 'hidden'
      }}>
        <Container component="main" sx={{ 
          pt: isPopupMode ? 1 : 4, 
          pb: isPopupMode ? 1 : 6,
          px: isPopupMode ? { xs: 1, sm: 1.5 } : undefined,
          maxWidth: isPopupMode ? '100% !important' : undefined
        }}>
          <Outlet />
        </Container>
      </Box>
      
      {/* 스크롤 탑 버튼 (팝업 모드에서는 숨김) */}
      {!isPopupMode && (
        <Fab
          color="primary"
          size="medium"
          aria-label="scroll to top"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            opacity: showScrollTop ? 1 : 0,
            transform: showScrollTop ? 'scale(1)' : 'scale(0)',
            transition: 'all 0.3s ease',
            background: '#1565C0',
            '&:hover': {
              background: '#0D47A1',
              transform: showScrollTop ? 'scale(1.1)' : 'scale(0)',
              boxShadow: '0 6px 20px rgba(21, 101, 192, 0.4)',
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
      
      {/* 푸터 (팝업 모드에서는 숨김) */}
      {!isPopupMode && (
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            mt: 'auto',
            backgroundColor: 'white',
            textAlign: 'center',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)'
          }}
        >
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ 
            color: '#4A5568',
            fontWeight: 500
          }}>
            © {new Date().getFullYear()} KOREA MEDIS
          </Typography>
        </Container>
      </Box>
      )}
    </>
  );
};

export default MainLayout; 